import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import jwt_decode from "jwt-decode";
import { useDispatch } from "react-redux";
import {
  CYPRESS_MEMBERSHIP_REACTIVATION_COMPLETE_EVENT,
  CYPRESS_MEMBERSHIP_REACTIVATION_TIMEOUT_EVENT
} from "constants/cypressMembershipTestEvents";
import { setCookie } from "utils/v3/CookieUtil";
import {
  getGlMemberStatusFromIdTokenClaims,
  getIsMemberActiveFromIdTokenClaims,
  getShareEligibilityFromIdTokenClaims
} from "utils/v3/auth0ClaimsUtils";
import { updateFieldsInUserPersonalInformation } from "store/reducers/user/UserAccountSlice";

const POLL_MS = 1000;
const MAX_WAIT_MS = 45000;
const LONG_WAIT_MESSAGE_MS = 15000;

export type UseMembershipActivationAfterPaymentOptions = {
  onActivated: () => void;
  onActivationTimeout: () => void;
};

export function useMembershipActivationAfterPayment({
  onActivated,
  onActivationTimeout
}: UseMembershipActivationAfterPaymentOptions) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [waitingForActivation, setWaitingForActivation] = useState(false);
  const [showLongWaitCopy, setShowLongWaitCopy] = useState(false);
  const pollDoneRef = useRef(false);
  const activationStartedRef = useRef(false);
  const onActivatedRef = useRef(onActivated);
  const onActivationTimeoutRef = useRef(onActivationTimeout);
  onActivatedRef.current = onActivated;
  onActivationTimeoutRef.current = onActivationTimeout;

  const beginActivationWait = useCallback(() => {
    if (activationStartedRef.current) return;
    activationStartedRef.current = true;
    pollDoneRef.current = false;
    setWaitingForActivation(true);
  }, []);

  useEffect(() => {
    if (!waitingForActivation) {
      setShowLongWaitCopy(false);
      return;
    }
    setShowLongWaitCopy(false);
    const longWaitTimer = globalThis.setTimeout(() => {
      setShowLongWaitCopy(true);
    }, LONG_WAIT_MESSAGE_MS);
    return () => globalThis.clearTimeout(longWaitTimer);
  }, [waitingForActivation]);

  useEffect(() => {
    if (!waitingForActivation) return;
    const win = globalThis as unknown as Window & { Cypress?: unknown };
    if (!win.Cypress) return;

    const onCypressMockActivationComplete = () => {
      if (pollDoneRef.current) return;
      pollDoneRef.current = true;
      dispatch(
        updateFieldsInUserPersonalInformation({
          isMemberActive: true
        })
      );
      onActivatedRef.current();
    };

    const onCypressMockActivationTimeout = () => {
      if (pollDoneRef.current) return;
      pollDoneRef.current = true;
      onActivationTimeoutRef.current();
    };

    win.addEventListener(
      CYPRESS_MEMBERSHIP_REACTIVATION_COMPLETE_EVENT,
      onCypressMockActivationComplete
    );
    win.addEventListener(
      CYPRESS_MEMBERSHIP_REACTIVATION_TIMEOUT_EVENT,
      onCypressMockActivationTimeout
    );
    return () => {
      win.removeEventListener(
        CYPRESS_MEMBERSHIP_REACTIVATION_COMPLETE_EVENT,
        onCypressMockActivationComplete
      );
      win.removeEventListener(
        CYPRESS_MEMBERSHIP_REACTIVATION_TIMEOUT_EVENT,
        onCypressMockActivationTimeout
      );
    };
  }, [waitingForActivation, dispatch]);

  useEffect(() => {
    if (!waitingForActivation) return;

    const startedAt = Date.now();
    let intervalId: number | undefined;

    const pollOnce = async () => {
      if (pollDoneRef.current) return;

      try {
        const tokenResponse = await getAccessTokenSilently({
          cacheMode: "off",
          detailedResponse: true
        });

        let claims: unknown;
        if (
          typeof tokenResponse === "object" &&
          tokenResponse !== null &&
          "access_token" in tokenResponse
        ) {
          const verbose = tokenResponse as {
            access_token: string;
            id_token?: string;
          };
          setCookie("accessToken", verbose.access_token);
          claims = verbose.id_token
            ? jwt_decode(verbose.id_token)
            : await getIdTokenClaims();
        } else if (typeof tokenResponse === "string") {
          setCookie("accessToken", tokenResponse);
          claims = await getIdTokenClaims();
        } else {
          claims = await getIdTokenClaims();
        }

        if (pollDoneRef.current) return;

        if (claims) {
          const active = getIsMemberActiveFromIdTokenClaims(claims);
          const glMemberStatus = getGlMemberStatusFromIdTokenClaims(claims);
          dispatch(
            updateFieldsInUserPersonalInformation({
              isMemberActive: active,
              // Only overwrite when the claim is present; activation may take
              // a few polls and we should not clear a previously seen status.
              ...(glMemberStatus == null ? {} : { glMemberStatus }),
              ...getShareEligibilityFromIdTokenClaims(claims)
            })
          );

          if (active) {
            pollDoneRef.current = true;
            onActivatedRef.current();
            return;
          }
        }

        if (Date.now() - startedAt >= MAX_WAIT_MS) {
          pollDoneRef.current = true;
          onActivationTimeoutRef.current();
        }
      } catch {
        if (pollDoneRef.current) return;
        if (Date.now() - startedAt >= MAX_WAIT_MS) {
          pollDoneRef.current = true;
          onActivationTimeoutRef.current();
        }
      }
    };

    void pollOnce();
    intervalId = globalThis.setInterval(() => {
      void pollOnce();
    }, POLL_MS) as unknown as number;

    return () => {
      if (intervalId != null) {
        globalThis.clearInterval(intervalId);
      }
    };
  }, [waitingForActivation, dispatch, getAccessTokenSilently, getIdTokenClaims]);

  return {
    beginActivationWait,
    showLongWaitCopy,
    waitingForActivation
  };
}
